import { ButtonProps } from "@timeedit/ui-components";
import WarningOutlined from "@timeedit/ui-components/icons/WarningOutlined";
import { ReactNode } from "react";
import { ConflictNotification } from "../../Conflict";
import { ButtonWithDescription } from "../ButtonWithDescription";
import { Translations, WaitlistButtonProps } from "../types";
import { IsOnWaitlistButton } from "./IsOnWaitlist";

type NotAllocatedProps = {
  buttonProps: ButtonProps;
  description: ReactNode;
  clash: boolean;
  linkedIssue: boolean;
  translations: Translations;
  onAllocate: () => void;
  waitlistProps: WaitlistButtonProps;
  groupName: string;
  activityName: string;
};

export function NotAllocated({
  buttonProps,
  description,
  clash,
  linkedIssue,
  translations,
  onAllocate,
  waitlistProps,
  groupName,
  activityName,
}: NotAllocatedProps): React.JSX.Element {
  const { isOnWaitlist, onLeaveWaitlist, waitlistState } = waitlistProps;

  if (isOnWaitlist) {
    return (
      <IsOnWaitlistButton
        onLeaveWaitlist={onLeaveWaitlist}
        translations={translations}
        activityName={activityName}
        buttonProps={buttonProps}
        description={description}
        linkedIssue={linkedIssue}
        groupName={groupName}
        clash={clash}
      />
    );
  }

  if (waitlistState.shouldShowWaitlist) {
    return (
      <NotAllocatedWaitlistButton
        waitlistProps={waitlistProps}
        translations={translations}
        buttonProps={buttonProps}
        description={description}
        linkedIssue={linkedIssue}
        clash={clash}
      />
    );
  }
  return (
    <NotAllocatedDefaultButton
      translations={translations}
      buttonProps={buttonProps}
      description={description}
      linkedIssue={linkedIssue}
      onAllocate={onAllocate}
      clash={clash}
    />
  );
}

function NotAllocatedDefaultButton({
  buttonProps,
  clash,
  description,
  linkedIssue,
  onAllocate,
  translations,
}: Omit<
  NotAllocatedProps,
  "waitlistProps" | "groupName" | "activityName"
>): React.JSX.Element {
  return (
    <>
      <ButtonWithDescription
        {...buttonProps}
        description={description}
        onClick={onAllocate}
        label={translations.registerTo}
      >
        {translations.register}
      </ButtonWithDescription>
      {clash && <ConflictNotification useText />}
      {linkedIssue && (
        <WarningOutlined
          className={"allocation-container--icon"}
          alt="Linked issue present"
        />
      )}
    </>
  );
}

type NotAllocatedWaitlistButtonProps = Omit<
  NotAllocatedProps,
  "onAllocate" | "groupName" | "activityName"
>;

function NotAllocatedWaitlistButton({
  buttonProps,
  clash,
  description,
  linkedIssue,
  translations,
  waitlistProps,
}: NotAllocatedWaitlistButtonProps): React.JSX.Element {
  return (
    <>
      <ButtonWithDescription
        {...buttonProps}
        description={description}
        onClick={waitlistProps.onJoinWaitlist}
        label={translations.addToWaitlist}
      >
        {translations.waitlist}
      </ButtonWithDescription>
      {clash && <ConflictNotification useText />}
      {linkedIssue && (
        <WarningOutlined
          className={"allocation-container--icon"}
          alt="Linked issue present"
        />
      )}
    </>
  );
}
